/* eslint-disable turbo/no-undeclared-env-vars */
"use client";
import { ReactNode, useMemo } from "react";
import { useAuth0 } from "@equipmentshare/auth0-react";
import {
  ApolloClient,
  InMemoryCache,
  ApolloProvider as ApolloProviderComponent,
  createHttpLink,
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { RetryLink } from "@apollo/client/link/retry";
import { useFleetGraphqlApiUrl } from "./fleet-graphql-api-provider";

const tokenOverride = process.env.NEXT_PUBLIC_TOKEN_OVERRIDE;

if (!process.env.NEXT_PUBLIC_GRAPHQL_API)
  console.warn("[ApolloProvider] NEXT_PUBLIC_GRAPHQL_API is not set");

const retryLink = new RetryLink({
  delay: { initial: 300, max: Infinity, jitter: true },
  attempts: (count, operation, error) => {
    const { retryCount = 5 } = operation.getContext();
    if (count >= retryCount) return false;
    if (error?.response?.status === 401) return false;
    return true;
  },
});
export default function ApolloProvider({ children }: { children: ReactNode }) {
  const gqlApi = useFleetGraphqlApiUrl();
  const { getAccessTokenSilently: auth0GetToken } = useAuth0();
  const getAccessTokenSilently = useMemo(() => {
    return tokenOverride ? () => tokenOverride : auth0GetToken;
  }, [auth0GetToken]);

  const client = useMemo(() => {
    const httpLink = createHttpLink({
      uri: (operation) => {
        const operationName = operation.operationName;
        return `${gqlApi}?op=${encodeURIComponent(operationName)}`;
      },
    });
    const authLink = setContext(async (_, { headers }) => {
      const token = await getAccessTokenSilently();
      return {
        headers: { ...headers, authorization: token ? `Bearer ${token}` : "" },
      };
    });

    return new ApolloClient({
      name: "optimization-app",
      link: authLink.concat(retryLink.concat(httpLink)),
      cache: new InMemoryCache(),
    });
  }, [gqlApi, getAccessTokenSilently]);

  return (
    <ApolloProviderComponent client={client}>
      {children}
    </ApolloProviderComponent>
  );
}
